import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Avatar from '@material-ui/core/Avatar';
import SiteIcon from '@material-ui/icons/Place';
import ListItemText from '@material-ui/core/ListItemText';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import {
    withOneplaceLibraryContext, IOneplaceLibraryContextProp,
    TabsComponent, QuestionGroup, ITabDefinition, IChecklistGroup,
    IChecklistField, IChecklistConditionalRule, ISite,
    hasConditionApplied, isConditionalRuleShow, findChecklistGroupById,
    getRenderProps, updateQuestionNumbers, validateChecklist, CheckCircleCustomColor, updateDynamicDatalistQuestions,
    getExpectedAnswerForFormulaQuestion, refreshFormulaQuestions,
    TemplateTypes
} from 'oneplace-components';
import { LoadStatus } from '../common/types';
import { Api } from '../../data_sources/api';
import { IIncidentReportSurveyResponse, IIncidentV2 } from '../../models/Incident';
import { initNewChecklistData } from '../checklists/utils/checklistData';
import styles from '../checklists/ChecklistStyles';
import { IncidentV2Info } from './IncidentV2Info';
import { IncidentSignHeader } from './IncidentSignHeader';
import { IncidentReportFooter } from './IncidentReportFooter';
import { IncidentFinish } from './IncidentFinish';
import HomeDialog from '../home/HomeDialog';
import { i18n } from '../../i18n';

/*
 * Public, no-login page: Home.tsx's welcome dialog + Site picker, then Checklist.tsx's INCIDENT
 * rendering branch over a blank incident. Creates a new incident on submit, rather than signing an
 * existing one like IncidentSign.tsx. An incident report is always attached to a single Site, so
 * there is no Franchisee picker here, unlike the checklist survey.
 */

export interface IIncidentReportProps extends
    RouteComponentProps<any>,
    WithStyles<typeof styles>,
    IOneplaceLibraryContextProp {
}

export interface IIncidentReportState {
    loadStatus: LoadStatus;
    errorMessage: string | null;
    surveyResponse: IIncidentReportSurveyResponse | null;
    showWelcomeDialog: boolean;
    site: ISite | null;
    incident: IIncidentV2 | null;
    renderProps: any;
    showValidationErrorDialog: boolean;
    validationErrors: string[];
    showSubmitDialog: boolean;
    incompleteFields: boolean;
    disableTabs: boolean;
    questionsNeedRefresh: boolean;
    submitted: boolean;
}

class IncidentReportComponent extends React.Component<IIncidentReportProps, IIncidentReportState> {

    conditionalRulesList: IChecklistConditionalRule[] = [];

    constructor(props: IIncidentReportProps) {
        super(props);
        this.state = {
            loadStatus: 'loading',
            errorMessage: null,
            surveyResponse: null,
            showWelcomeDialog: true,
            site: null,
            incident: null,
            renderProps: null,
            showValidationErrorDialog: false,
            validationErrors: [],
            showSubmitDialog: false,
            incompleteFields: false,
            disableTabs: false,
            questionsNeedRefresh: false,
            submitted: false
        };
    }

    async componentDidMount() {
        if (document.title === '') {
            document.title =  `${i18n.t('customLabel_incident')}  Survey`;
        }
        await this.loadSurvey();
    }

    async loadSurvey() {
        try {
            const region = this.props.match.params.region;
            const path = this.props.match.params.path;
            const api = new Api(region, path);
            const surveyResponse: IIncidentReportSurveyResponse = await api.fetchIncidentReportSurvey();

            if (surveyResponse.success != null && !surveyResponse.success) {
                this.setState({ errorMessage: surveyResponse.data || 'Unable to load survey', loadStatus: 'loaded' });
                return;
            }

            if (surveyResponse.messages && this.props.ctx.i18next.addResourceBundle) {
                this.props.ctx.i18next.addResourceBundle(
                    this.props.ctx.i18next.language, 'common', surveyResponse.messages);
            }

            this.setState({ surveyResponse, loadStatus: 'loaded' });

            const sites = surveyResponse.assignments.sites;
            if (sites && sites.length === 1) {
                // only one Site assigned to this survey - skip the picker entirely
                this.selectSite(sites[0]);
            }
        } catch (e) {
            this.setState({ loadStatus: 'load_error' });
        }
    }

    onConfirmWelcomeDialog = () => {
        this.setState({ showWelcomeDialog: false });
    }

    selectSite = (site: ISite) => {
        const incident = this.state.surveyResponse!.incident;

        initNewChecklistData(incident, { assigneeType: 'site', site });

        let renderProps = getRenderProps({
            template: incident,
            assigneeType: 'site',
            assigneeAttributes: site.attributes
        });
        renderProps = updateQuestionNumbers(incident, renderProps!);

        this.setState({ site, incident, renderProps, showWelcomeDialog: false }, () => {
            this.findConditionalRulesFromTemplate();
        });
    }

    findConditionalRulesFromTemplate() {
        this.state.incident!.groups.forEach((group) => {
            group.fields.filter((f) => f.conditionalRule != null)
                .forEach((field) => this.conditionalRulesList.push(field.conditionalRule!));
        });
    }

    onFieldChange = (_groupIndex: number, _fieldId: number) => {
        this.setState({ disableTabs: false });
    }

    getControlQuestionValue = (groupId: number, questionId: number): number => {
        const group = this.state.incident?.groups.find(g => g.id === Number(groupId));
        if (!group) return 0;
        const question = group.fields.find(f => f.id === Number(questionId));
        if (!question) return 0;
        if (question.type === 'number') {
            return Number(question.value) || 0;
        } else if (question.type === 'select_many') {
            return Array.isArray(question.values) ? question.values.length : 0;
        }
        return 0;
    }

    validateConditionalQuestion = (newValue: string, question: IChecklistField | null) => {
        if (question == null) {
            this.setState({ questionsNeedRefresh: false });
            return;
        }
        const filteredConditionalRules: IChecklistConditionalRule[] = [];
        question.conditions!.forEach((conditionId) => {
            this.conditionalRulesList.forEach((conditionRule) => {
                conditionRule.conditions!.forEach((condition) => {
                    if (condition.id === conditionId) {
                        condition.applied = hasConditionApplied(newValue, question, condition);
                        if (filteredConditionalRules.indexOf(conditionRule) < 0) {
                            filteredConditionalRules.push(conditionRule);
                        }
                    }
                });
            });
        });
        filteredConditionalRules.forEach((conditionalRule) => {
            this.refreshConditionalRuleAndSubQuestions(conditionalRule);
        });
        const renderProps = updateQuestionNumbers(this.state.incident!, this.state.renderProps!);
        this.setState({
            questionsNeedRefresh: filteredConditionalRules.length > 0,
            renderProps
        });
    }

    refreshConditionalRuleAndSubQuestions = (conditionalRule: IChecklistConditionalRule) => {
        const currentGroup = findChecklistGroupById(conditionalRule.groupId, this.state.incident!);
        const showFieldFlag = isConditionalRuleShow(conditionalRule, this.conditionalRulesList);
        currentGroup!.fields.forEach((field) => {
            if (conditionalRule.conditionalQuestions.includes(field.id)) {
                field.hidden = !showFieldFlag;
                if (field.type === 'dynamicdatalist') {
                    updateDynamicDatalistQuestions(field);
                }
                if (field.type === 'conditionalRule') {
                    this.refreshConditionalRuleAndSubQuestions(field.conditionalRule!);
                }
            }
        });
    }

    refreshFormulaQuestionsFn = (inputQuestion: IChecklistField) => {
        const formulaQuestions = getExpectedAnswerForFormulaQuestion(
            inputQuestion, undefined, this.state.incident!);
        if (formulaQuestions && formulaQuestions.length > 0) {
            const renderProps = refreshFormulaQuestions(
                this.state.incident!, this.state.renderProps!, formulaQuestions);
            this.setState({ questionsNeedRefresh: true, renderProps });
        }
    }

    onSubmitIncident = () => {
        const errors = validateChecklist(this.state.incident!, this.state.renderProps!, i18n.t.bind(i18n));
        if (errors && errors.length) {
            this.setState({
                showValidationErrorDialog: true,
                validationErrors: errors.map((error: any) => error.errorText)
            });
            return;
        }
        let showIncompleteFields = false;
        this.state.renderProps?.groups.forEach((group: any) => {
            if (group.incompleteFields) showIncompleteFields = true;
        });
        this.setState({ showSubmitDialog: true, incompleteFields: showIncompleteFields });
    }

    onSubmitted = () => {
        this.setState({ submitted: true });
    }

    render() {
        const t = (key: string) => this.props.ctx.i18next.t(key);

        if (this.state.submitted) {
            return <IncidentFinish />;
        }

        if (this.state.errorMessage) {
            return (
                <div style={{ textAlign: 'center', marginTop: 64 }}>
                    <Typography variant="h5" color="error">{this.state.errorMessage}</Typography>
                </div>
            );
        }

        if (this.state.loadStatus === 'load_error') {
            return (
                <div style={{ textAlign: 'center', marginTop: 64 }}>
                    <Typography variant="h5" color="error">{t('data_load_error')}</Typography>
                </div>
            );
        }

        if (this.state.loadStatus === 'loading' || !this.state.surveyResponse) {
            return null;
        }

        if (this.state.showWelcomeDialog) {
            const survey = this.state.surveyResponse.survey;
            return <HomeDialog
                title={survey.name}
                message={survey.message}
                isOpen={true}
                onConfirm={this.onConfirmWelcomeDialog} />;
        }

        // more than one Site assigned to this survey and none picked yet - show the picker
        if (!this.state.site) {
            const sites = this.state.surveyResponse.assignments.sites || [];
            return (
                <div className={this.props.classes.appWrapper} style={{ marginTop: 64 }}>
                    <Card>
                        <CardContent style={{ padding: 16 }}>
                            <Typography variant="h6" style={{ marginBottom: 8 }}>
                                {t('customLabel_sites')}
                            </Typography>
                            <List disablePadding={true}>
                                {sites.map((site) => (
                                    <ListItem button style={{ padding: '8px' }} key={site.id}
                                        onClick={() => this.selectSite(site)}>
                                        <Avatar>
                                            <SiteIcon />
                                        </Avatar>
                                        <ListItemText style={{marginLeft: 10}}
                                                      primary={ `${site.franchisee?.name} - ${site.name}` }/>
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </div>
            );
        }

        const incident = this.state.incident!;
        const region = this.props.match.params.region;
        const path = this.props.match.params.path;

        let content;
        if (incident.useTabs) {
            const groupsToShow: IChecklistGroup[] = [];
            incident.groups.forEach((group, index) => {
                if (this.state.renderProps!.groups[index].showTab) {
                    groupsToShow.push(group);
                }
            });

            const tabs: ITabDefinition[] = groupsToShow.map((group, index) => {
                let groupIndex = 0;
                for (let i = 0; i < incident.groups.length; i++) {
                    if (incident.groups[i].id === group.id) {
                        groupIndex = i;
                        break;
                    }
                }
                const renderProps = this.state.renderProps!.groups[groupIndex];
                return {
                    name: 'group_' + index,
                    label: <div style={{ display: 'flex', alignItems: 'center' }}>
                        {group.label}
                        {!renderProps.incompleteFields &&
                            <CheckCircleCustomColor size={20} background="#ff5983" color="#ffffff" />}
                    </div>,
                    component: <QuestionGroup
                        groupIndex={index}
                        group={group}
                        renderProps={renderProps!}
                        dateTimeFormat={t('dateTimeFormat')}
                        dateFormat={t('dateFormat')}
                        onFieldChange={this.onFieldChange}
                        onTicketPreset={() => { }}
                        validateConditionalQuestion={this.validateConditionalQuestion}
                        questionsNeedRefresh={this.state.questionsNeedRefresh}
                        hideGroupPhotos={true}
                        hideQuestionNumber={incident.hideQuestionNumber}
                        disableTabLinks={this.state.disableTabs}
                        refreshFormulaQuestions={this.refreshFormulaQuestionsFn}
                        getControlQuestionValue={this.getControlQuestionValue}
                        templateType={TemplateTypes.INCIDENT}
                    />
                };
            });

            content = <TabsComponent
                color="light"
                compact={true}
                tabs={tabs}
                rememberScrollPosition={true}
                initialTabIdx={0}
                hasDetailsTab={false}
                disabled={this.state.disableTabs}
            />;
        } else {
            content = incident.groups.map((group, index) => {
                const renderProps = this.state.renderProps!.groups[index];
                return (
                    renderProps.showTab && <div key={group.id}>
                        <Typography variant="h5" className={this.props.classes.groupHeader}>
                            {group.label}
                        </Typography>
                        <QuestionGroup
                            groupIndex={index}
                            group={group}
                            renderProps={renderProps}
                            onFieldChange={this.onFieldChange}
                            onTicketPreset={() => { }}
                            validateConditionalQuestion={this.validateConditionalQuestion}
                            questionsNeedRefresh={this.state.questionsNeedRefresh}
                            dateTimeFormat={t('dateTimeFormat')}
                            dateFormat={t('dateFormat')}
                            hideGroupPhotos={true}
                            hideQuestionNumber={incident.hideQuestionNumber}
                            disableTabLinks={this.state.disableTabs}
                            refreshFormulaQuestions={this.refreshFormulaQuestionsFn}
                            getControlQuestionValue={this.getControlQuestionValue}
                            templateType={TemplateTypes.INCIDENT}
                        />
                    </div>
                );
            });
        }

        return (
            <>
                <IncidentSignHeader onSubmitIncident={this.onSubmitIncident} />
                <div id="incident-report" className={this.props.classes.appWrapper}>
                    <IncidentV2Info
                        surveyName={this.state.surveyResponse.survey.name}
                        incident={incident}
                    />
                    {content}
                    <IncidentReportFooter
                        incident={incident}
                        surveyName={this.state.surveyResponse.survey.name}
                        allowAttachIncidentPdf={this.state.surveyResponse.survey.allowAttachIncidentPdf}
                        errorsShown={this.state.showValidationErrorDialog}
                        errors={this.state.validationErrors}
                        onErrorsDismissed={() => this.setState({ showValidationErrorDialog: false })}
                        onCloseSubmitDialog={() => this.setState({ showSubmitDialog: false })}
                        isSubmitDialogOpen={this.state.showSubmitDialog}
                        region={region}
                        path={path}
                        onSubmitted={this.onSubmitted}
                    />
                </div>
            </>
        );
    }
}

export const IncidentReport = withStyles(styles)(withRouter(withOneplaceLibraryContext(IncidentReportComponent)));
export default IncidentReport;
